import { BACKEND_URL } from './constants';

export interface DailyPoint {
  date: string;
  points: number;
}
export interface PointsStats {
  total_points: number;
  points_today: number;
  daily: DailyPoint[];
}

export interface StreakStatus {
  streak_multiplier: number;
  streak_expires_at: string | null;
  points_this_streak: number;
}

export interface LeaderboardEntry {
  user_id: string;
  user_name: string;
  total_points: number;
  rank: number;
  level: number;
}

export interface LeaderboardResponse {
  leaderboard: LeaderboardEntry[];
  current_user_rank: LeaderboardEntry | null;
  /** UTC instant when the monthly board resets (1st of next month, app tz). */
  resets_at: string | null;
}

/** Media types usable as a leaderboard filter. */
export type LeaderboardMediaType =
  | 'audio'
  | 'video'
  | 'text'
  | 'image'
  | 'document';

/** The viewer's IANA timezone (e.g. "Asia/Kolkata"). */
export const getBrowserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

export const formatPoints = (value: number, locale?: string): string =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(
    Number.isFinite(value) ? value : 0,
  );

export const getPointsStats = async (
  token: string,
  userIdentifier: string,
  tz: string = getBrowserTimeZone(),
): Promise<PointsStats> => {
  const params = new URLSearchParams();
  if (tz) params.set('tz', tz);
  const res = await fetch(
    `${BACKEND_URL}/points/stats/${userIdentifier}?${params.toString()}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new Error('Failed to fetch points stats');
  return res.json();
};

export const getStreakStatus = async (token: string): Promise<StreakStatus> => {
  const res = await fetch(`${BACKEND_URL}/points/streak`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to fetch streak status');
  return res.json();
};

/**
 * Monthly leaderboard. Pass `categoryId` for a category board or `mediaType`
 * for a per-media board; omit both for the global board.
 */
export const getLeaderboard = async (
  token: string,
  options: { categoryId?: string; mediaType?: LeaderboardMediaType } = {},
): Promise<LeaderboardResponse> => {
  const params = new URLSearchParams();
  if (options.categoryId) params.set('category_id', options.categoryId);
  if (options.mediaType) params.set('media_type', options.mediaType);
  const qs = params.toString();
  const res = await fetch(
    `${BACKEND_URL}/points/leaderboard${qs ? `?${qs}` : ''}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new Error('Failed to fetch leaderboard');
  return res.json();
};

export interface MilestoneCriterion {
  key: string;
  label: string;
  current: number;
  target: number;
  met: boolean;
}

export interface MilestoneStatus {
  current_level: number;
  current_name: string;
  current_title: string;
  next_level: number | null;
  next_name: string | null;
  next_title: string | null;
  criteria: MilestoneCriterion[];
}

export const getMilestone = async (
  token: string,
  userIdentifier: string,
): Promise<MilestoneStatus> => {
  const res = await fetch(`${BACKEND_URL}/points/milestone/${userIdentifier}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to fetch milestone');
  return res.json();
};

export interface UserGamification {
  lifetime_points: number;
  level: number;
}

/**
 * Lifetime points + level for a user, read from the contributions endpoint
 * (where they are surfaced alongside `credits`). Both are computed server-side.
 */
export const getUserGamification = async (
  token: string,
  userIdentifier: string,
): Promise<UserGamification> => {
  const res = await fetch(
    `${BACKEND_URL}/users/${userIdentifier}/contributions`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new Error('Failed to fetch user gamification');
  const data = await res.json();
  return {
    lifetime_points: data.lifetime_points ?? 0,
    level: data.level ?? 1,
  };
};
